import type { PlayerMatchStatWithPlayer } from '@/lib/data/provider';

/**
 * A 0-10 player rating DERIVED from match stats. The data provider only rates
 * some players (often just starters / heavily-involved players), leaving many
 * with no rating - so we compute one from the stats we already store, the way a
 * football rating reads: a ~6.0 baseline, lifted by goals / assists / saves /
 * clean sheets and dragged down by cards / own-goals / goals conceded, weighted
 * by position. Deterministic and position-aware.
 */
function rate(s: {
  position: string;
  minutes: number;
  goals: number;
  assists: number;
  passes: number;
  tackles: number;
  saves: number;
  goalsConceded: number;
  yellow: number;
  red: number;
  penaltySaved: number;
  penaltyMissed: number;
  ownGoals: number;
}): number {
  const pos = s.position;
  let r = 6.0;
  const goalVal = pos === 'GK' ? 2.5 : pos === 'DEF' ? 1.8 : pos === 'MID' ? 1.4 : 1.15;
  r += s.goals * goalVal;
  r += s.assists * 1.0;

  if (pos === 'GK') {
    r += Math.min(s.saves, 8) * 0.14;
    r += s.penaltySaved * 1.2;
    if (s.minutes >= 60 && s.goalsConceded === 0) r += 1.0;
    r -= s.goalsConceded * 0.3;
  } else if (pos === 'DEF') {
    if (s.minutes >= 60 && s.goalsConceded === 0) r += 0.7;
    r -= Math.max(0, s.goalsConceded - 1) * 0.12;
    r += Math.min(s.tackles, 6) * 0.06;
  } else {
    r += Math.min(s.tackles, 6) * 0.04;
  }

  r += Math.min(s.passes, 90) * 0.004; // involvement, up to +0.36
  r -= s.yellow * 0.4;
  r -= s.red * 1.8;
  r -= s.ownGoals * 1.3;
  r -= s.penaltyMissed * 0.9;

  // A short cameo hovers near the baseline (small sample).
  if (s.minutes > 0 && s.minutes < 20) r = 6.0 + (r - 6.0) * 0.6;

  return Math.max(4.0, Math.min(9.7, Math.round(r * 10) / 10));
}

export interface EffectiveRating {
  value: number | null;
  /** True when computed from stats (no official provider rating). */
  derived: boolean;
}

/**
 * The rating to SHOW: the official provider rating when present, otherwise a
 * derived one for any player who actually played (minutes > 0). Unused subs
 * (0 minutes) stay unrated.
 */
export function effectiveRating(stat: PlayerMatchStatWithPlayer): EffectiveRating {
  if (stat.rating != null) return { value: stat.rating, derived: false };
  if (stat.minutes > 0) {
    return {
      value: rate({
        position: stat.player?.position ?? 'MID',
        minutes: stat.minutes,
        goals: stat.goals,
        assists: stat.assists,
        passes: stat.passes,
        tackles: stat.tackles,
        saves: stat.saves,
        goalsConceded: stat.goals_conceded,
        yellow: stat.yellow,
        red: stat.red,
        penaltySaved: stat.penalty_saved,
        penaltyMissed: stat.penalty_missed,
        ownGoals: stat.own_goals,
      }),
      derived: true,
    };
  }
  return { value: null, derived: false };
}
